import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, Phone, Mail, Bus, Train, Car, CheckCircle2, ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import api from "../api/client";
import { useLanguage } from "../context/LanguageContext";

export default function CentreDetail() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [centre, setCentre] = useState(null);
  const [staff, setStaff] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setNotFound(false);
    api.get(`/centres/${slug}`).then((r) => setCentre(r.data)).catch(() => setNotFound(true));
    api.get("/staff").then((r) => setStaff(r.data.filter((s) => s.centre_slug === slug))).catch(() => {});
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    if (!centre?.gallery?.length || centre.gallery.length < 2) return;
    const timer = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % centre.gallery.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [centre]);

  if (notFound) return <Navigate to="/centres" replace />;
  if (!centre) return <div className="min-h-screen bg-white"><Navbar /></div>;

  const l = centre.i18n?.[language] || centre.i18n?.fr || {};
  const gallery = centre.gallery?.length ? centre.gallery : (centre.photo ? [centre.photo] : []);

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      <Seo title={`${centre.name} – ${centre.address_line}, ${centre.postal_code} ${centre.city}`} description={((l.tagline ? `${l.tagline}. ` : "") + (l.description || "")).slice(0, 160)} image={centre.photo} />

      {/* Hero image */}
      {centre.photo && (
        <div className="pt-16 w-full h-72 sm:h-[420px] overflow-hidden bg-slate-100">
          <img src={centre.photo} alt={centre.name} className="w-full h-full object-cover" />
        </div>
      )}

      <section className={centre.photo ? "py-14" : "pt-32 pb-14"}>
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <Link
            to="/centres"
            className="inline-flex items-center gap-2 text-[#2E4DB5] text-sm font-semibold hover:opacity-70 transition-opacity mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> {t.centres.back}
          </Link>

          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#2E4DB5] mb-3">
            {t.centres.eyebrow}
          </p>
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4" data-testid="centre-name">
            {centre.name}
          </h1>
          <p className="text-lg text-slate-600 mb-10">{l.tagline}</p>

          <div className="h-px bg-gradient-to-r from-[#2E4DB5] via-[#3B9DA5] to-transparent mb-10" />

          {l.description && <p className="text-slate-700 leading-relaxed mb-10 text-[15px]">{l.description}</p>}

          {l.features && l.features.length > 0 && (
            <div className="mb-14">
              <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-5">{t.centres.features}</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {l.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 bg-slate-50/70 border border-slate-100 rounded-2xl px-4 py-3">
                    <CheckCircle2 className="w-5 h-5 text-[#3B9DA5] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 1 && (
        <section className="pb-14">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-5">{t.centres.gallery}</h2>
            <div className="relative aspect-[16/10] rounded-3xl overflow-hidden shadow-xl bg-slate-100">
              {gallery.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === currentSlide ? "opacity-100" : "opacity-0"}`}
                />
              ))}
              <div className="absolute top-4 right-4 bg-slate-900/55 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                {currentSlide + 1} / {gallery.length}
              </div>
            </div>
            <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setCurrentSlide(i)}
                  className={`flex-shrink-0 w-20 h-14 rounded-xl overflow-hidden transition-all ${i === currentSlide ? "ring-2 ring-[#2E4DB5] ring-offset-2" : "opacity-70 hover:opacity-100"}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact + Transport */}
      <section className="py-14 bg-slate-50/70">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-5">{t.centres.contact}</h2>
            <ul className="space-y-4 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#3B9DA5] flex-shrink-0 mt-0.5" />
                <div>
                  {centre.address_line}<br />
                  {centre.postal_code} {centre.city}
                </div>
              </li>
              {centre.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#3B9DA5] flex-shrink-0" />
                  <a href={`tel:${centre.phone.replace(/\s/g, "")}`} className="hover:text-[#2E4DB5]">{centre.phone}</a>
                </li>
              )}
              {centre.email && (
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#3B9DA5] flex-shrink-0" />
                  <a href={`mailto:${centre.email}`} className="hover:text-[#2E4DB5] break-all">{centre.email}</a>
                </li>
              )}
            </ul>
          </div>
          {centre.transport && (
            <div>
              <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-5">{t.centres.transport}</h2>
              <ul className="space-y-3 text-sm text-slate-700">
                {centre.transport.metro && (<li className="flex items-center gap-3"><Train className="w-5 h-5 text-[#3B9DA5]" /> <span><strong>Métro:</strong> {centre.transport.metro}</span></li>)}
                {centre.transport.tram && (<li className="flex items-center gap-3"><Train className="w-5 h-5 text-[#3B9DA5]" /> <span><strong>Tram:</strong> {centre.transport.tram}</span></li>)}
                {centre.transport.bus && (<li className="flex items-center gap-3"><Bus className="w-5 h-5 text-[#3B9DA5]" /> <span><strong>Bus:</strong> {centre.transport.bus}</span></li>)}
                {centre.transport.parking && (<li className="flex items-center gap-3"><Car className="w-5 h-5 text-[#3B9DA5]" /> <span><strong>Parking:</strong> {centre.transport.parking}</span></li>)}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Team at this centre */}
      {staff.length > 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">{t.centres.team}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {staff.map((m) => (
                <Link
                  key={m.id}
                  to={m.has_detail ? `/equipe/${m.slug}` : `/equipe#staff-${m.id}`}
                  className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden"
                >
                  <div className="aspect-square overflow-hidden bg-slate-100">
                    {m.photo ? (
                      <img src={m.photo} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${m.gradient || "from-blue-500 to-cyan-500"} opacity-30`} />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#2E4DB5] mb-0.5">
                      {m.i18n?.[language]?.title || m.i18n?.fr?.title || m.specialty}
                    </p>
                    <p className="font-outfit font-semibold text-sm text-slate-900 leading-tight">{m.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
